import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import {
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material'
import { Button } from 'vnetwork-ui'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import {
    FilterBar,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    getAffiliatePayouts,
    updatePayout,
    getPayoutCommissions,
} from '@/modules/Affiliate/services'
import { getPayoutStatusFilterOptions } from '@/modules/Affiliate/constants'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Payout, PayoutCommission } from '@/modules/Affiliate/types'
import { formatFromUTC } from '@/utils/function'
import CheckCircleIcons from '@/icons/CheckCircleIcons'
import CancelIcons from '@/icons/CancelIcons'
import EyeIcons from '@/icons/EyeIcons'
import useAffiliatePolicyPermissions from '@/modules/Affiliate/hooks/useAffiliatePolicyPermissions'

interface PayoutsProps {
    affiliateId: number
}

interface PayoutFilters {
    status: string
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
    }).format(value)
}

const Payouts: React.FC<PayoutsProps> = ({ affiliateId }) => {
    const { t } = useTranslation('affiliate')
    const { canUpdateAffiliate } = useAffiliatePolicyPermissions()
    const queryClient = useQueryClient()
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false)
    const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null)
    const [rejectionReason, setRejectionReason] = useState('')
    const [transactionRef, setTransactionRef] = useState('')
    const [detailsPayout, setDetailsPayout] = useState<Payout | null>(null)

    const {
        params,
        filters,
        queryParams,
        setParam,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<PayoutFilters>({
        defaults: { page: 1, page_size: 10 },
        filterDefaults: { status: '' },
    })

    const { control } = useForm<PayoutFilters>({
        defaultValues: filters,
        values: filters,
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.AFFILIATE_PAYOUTS,
            'affiliate-payouts',
            affiliateId,
            queryParams,
            filters,
        ] as const,
        queryFn: () =>
            getAffiliatePayouts(affiliateId, {
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                status: filters?.status as string | undefined,
            }),
        enabled: !!affiliateId,
        placeholderData: true,
    })

    const payouts = data?.results || []
    const pagination = data?.pagination
    const stateComponent = renderState()

    // Line items of the payout opened in the details dialog — which commissions (and which months)
    // the settlement batch actually covers. Fetched only when a payout is selected.
    const { data: lineItems, renderState: renderLineItemsState } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.AFFILIATE_PAYOUTS,
                'payout-commissions',
                detailsPayout?.payout_id,
            ] as const,
            queryFn: () => getPayoutCommissions(detailsPayout!.payout_id),
            enabled: !!detailsPayout,
        })

    const commissionItems = (lineItems || []) as PayoutCommission[]

    // Actual covered span, derived from the swept commissions' invoice payment dates. A backlog
    // dc_monthly payout can reach back several months, so this may be wider than one month.
    const coveredRange = useMemo(() => {
        // effective_payment_at is COALESCE(invoice_payment_at, paid_at, confirmed_at) — stable even
        // after a post-payout refund NULLs the invoice's payment date, so the range never collapses.
        const dates = commissionItems
            .map((i) => i.effective_payment_at || i.invoice_payment_at)
            .filter((d): d is string => !!d)
            .sort()
        if (!dates.length) return null
        return { from: dates[0], to: dates[dates.length - 1] }
    }, [commissionItems])

    const { mutateAsync: approveMutation, isPending: isApproving } =
        useMutationWithHandler(
            { mutationFn: updatePayout },
            {
                reactQueryKeys: QUERY_KEYS.AFFILIATE_PAYOUTS,
                successMessage: t('payout_approved'),
            }
        )

    const { mutateAsync: rejectMutation, isPending: isRejecting } =
        useMutationWithHandler(
            { mutationFn: updatePayout },
            {
                reactQueryKeys: QUERY_KEYS.AFFILIATE_PAYOUTS,
                successMessage: t('payout_rejected'),
            }
        )

    const { mutateAsync: completeMutation, isPending: isCompleting } =
        useMutationWithHandler(
            { mutationFn: updatePayout },
            {
                reactQueryKeys: QUERY_KEYS.AFFILIATE_PAYOUTS,
                successMessage: t('payout_completed'),
            }
        )

    const handleApprove = async (payout: Payout) => {
        if (!canUpdateAffiliate) return
        await approveMutation({
            payout_id: payout.payout_id,
            payload: { status: 'approved' },
        })
        queryClient.invalidateQueries({
            queryKey: [
                QUERY_KEYS.AFFILIATE_PAYOUTS,
                'affiliate-payouts',
                affiliateId,
                queryParams,
                filters,
            ] as const,
        })
    }

    const handleRejectClick = (payout: Payout) => {
        setSelectedPayout(payout)
        setRejectDialogOpen(true)
    }

    const handleRejectConfirm = async () => {
        if (!canUpdateAffiliate) return
        if (selectedPayout) {
            await rejectMutation({
                payout_id: selectedPayout.payout_id,
                payload: {
                    // 'cancelled' is the real backend enum value for a denied/voided payout;
                    // 'rejected' is not in payout_status and would 500 at the DB layer.
                    status: 'cancelled',
                    rejection_reason: rejectionReason,
                },
            })
            setRejectDialogOpen(false)
            setSelectedPayout(null)
            setRejectionReason('')
            queryClient.invalidateQueries({
                queryKey: [
                    QUERY_KEYS.AFFILIATE_PAYOUTS,
                    'affiliate-payouts',
                    affiliateId,
                    queryParams,
                    filters,
                ] as const,
            })
        }
    }

    const handleCompleteClick = (payout: Payout) => {
        setSelectedPayout(payout)
        setTransactionRef('')
        setCompleteDialogOpen(true)
    }

    const handleCompleteConfirm = async () => {
        if (!canUpdateAffiliate) return
        if (selectedPayout) {
            await completeMutation({
                payout_id: selectedPayout.payout_id,
                payload: {
                    status: 'completed',
                    transaction_ref: transactionRef,
                },
            })
            setCompleteDialogOpen(false)
            setSelectedPayout(null)
            setTransactionRef('')
            queryClient.invalidateQueries({
                queryKey: [
                    QUERY_KEYS.AFFILIATE_PAYOUTS,
                    'affiliate-payouts',
                    affiliateId,
                    queryParams,
                    filters,
                ] as const,
            })
        }
    }

    const filterItems: FilterItemConfig[] = useMemo(
        () => [
            {
                name: 'status',
                type: 'select',
                label: t('status'),
                placeholder: t('status'),
                options: getPayoutStatusFilterOptions(t),
            },
        ],
        [t]
    )

    const columns: DataTableColumn<Payout>[] = useMemo(
        () => [
            {
                key: 'payout_id',
                title: t('payout_id'),
                width: 100,
            },
            {
                key: 'amount',
                title: t('amount'),
                width: 150,
                render: (value) => (
                    <span className="font-semibold">
                        {formatCurrency(value)}
                    </span>
                ),
            },
            {
                key: 'period_start',
                title: t('period'),
                width: 200,
                render: (_value, row) =>
                    `${row.period_start} - ${row.period_end}`,
            },
            {
                key: 'payout_method',
                title: t('payout_method'),
                width: 130,
                render: (value) => value || '-',
            },
            {
                key: 'status',
                title: t('status'),
                width: 120,
                formatter: {
                    // colorMap must map value -> { label, color }; the chip formatter ignores a
                    // separate labelMap and treats a bare color string as the config (losing color
                    // + label). Merge them into the supported shape.
                    type: 'chip',
                    colorMap: {
                        pending: { label: t('pending'), color: 'warning' },
                        approved: { label: t('approved'), color: 'info' },
                        processing: { label: t('processing'), color: 'info' },
                        completed: { label: t('completed'), color: 'success' },
                        failed: { label: t('failed'), color: 'error' },
                        cancelled: { label: t('cancelled'), color: 'default' },
                    },
                },
            },
            {
                key: 'transaction_ref',
                title: t('transaction_ref'),
                width: 150,
                render: (value) => (
                    <span className="font-mono">{value || '-'}</span>
                ),
            },
            {
                key: 'requested_at',
                title: t('requested_at'),
                width: 160,
                render: (value) => formatFromUTC(value, 'DD/MM/YYYY HH:mm'),
            },
            {
                key: 'actions',
                title: t('actions'),
                width: 130,
                render: (_value, row) => {
                    const canApprove =
                        canUpdateAffiliate && row.status === 'pending'
                    const canComplete =
                        canUpdateAffiliate &&
                        (row.status === 'approved' ||
                            row.status === 'processing')
                    const canReject =
                        canUpdateAffiliate &&
                        (row.status === 'pending' ||
                            row.status === 'approved' ||
                            row.status === 'processing')

                    return (
                        <div className="flex gap-1">
                            <Tooltip title={t('view_details')}>
                                <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => setDetailsPayout(row)}
                                >
                                    <EyeIcons className="w-4 h-4" />
                                </IconButton>
                            </Tooltip>
                            {canApprove && (
                                <Tooltip title={t('approve')}>
                                    <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleApprove(row)}
                                        disabled={isApproving}
                                    >
                                        <CheckCircleIcons className="w-4 h-4" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {canComplete && (
                                <Tooltip title={t('complete')}>
                                    <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleCompleteClick(row)}
                                        disabled={isCompleting}
                                    >
                                        <CheckCircleIcons className="w-4 h-4" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {canReject && (
                                <Tooltip title={t('reject')}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRejectClick(row)}
                                        disabled={isRejecting}
                                    >
                                        <CancelIcons className="w-4 h-4" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    )
                },
            },
        ],
        [t, isApproving, isRejecting, isCompleting, canUpdateAffiliate]
    )

    const handlePageChange = (page: number) => setParam('page', page, false)
    const handlePageSizeChange = (size: number) => {
        setParam('page_size', size)
        setParam('page', 1)
    }

    return (
        <>
            <Paper className="space-y-2 my-2">
                <FilterBar
                    control={control}
                    setFilter={setFilter}
                    items={filterItems}
                    hideSearch
                />
                {stateComponent || (
                    <DataTableCustomized<Payout>
                        columns={columns}
                        rows={payouts}
                        rowKey={(row) => row.payout_id}
                        size="small"
                        toolbar={{
                            search: {
                                ...searchInputProps,
                                placeholder: t('search_affiliate'),
                            },
                            settings: {
                                show: true,
                                storageKey: 'affiliate-payouts',
                            },
                        }}
                        stickyHeader={{
                            enabled: true,
                            offsetTopSelector: '#main-header',
                        }}
                        pagination={{
                            page: params.page,
                            pageSize: params.page_size,
                            total: pagination?.total || 0,
                            onPageChange: handlePageChange,
                            onPageSizeChange: handlePageSizeChange,
                        }}
                    />
                )}
            </Paper>

            {/* Reject Dialog */}
            <Dialog
                open={rejectDialogOpen && canUpdateAffiliate}
                onClose={() => setRejectDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('reject_payout')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('rejection_reason')}
                        fullWidth
                        multiline
                        rows={3}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setRejectDialogOpen(false)}
                        disabled={isRejecting}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleRejectConfirm}
                        color="error"
                        variant="contained"
                        disabled={isRejecting || !rejectionReason}
                    >
                        {isRejecting ? t('rejecting') : t('reject')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Complete Dialog */}
            <Dialog
                open={completeDialogOpen && canUpdateAffiliate}
                onClose={() => setCompleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('complete_payout')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('transaction_ref')}
                        placeholder={t('enter_transaction_ref')}
                        fullWidth
                        value={transactionRef}
                        onChange={(e) => setTransactionRef(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCompleteDialogOpen(false)}
                        disabled={isCompleting}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleCompleteConfirm}
                        color="success"
                        variant="contained"
                        disabled={isCompleting || !transactionRef}
                    >
                        {isCompleting ? t('completing') : t('complete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Details Dialog — line items (commissions) this settlement batch covers */}
            <Dialog
                open={!!detailsPayout}
                onClose={() => setDetailsPayout(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t('payout_details')}
                    {detailsPayout ? ` #${detailsPayout.payout_id}` : ''}
                </DialogTitle>
                <DialogContent>
                    {detailsPayout && (
                        <div className="mb-3 grid grid-cols-2 gap-2 text-sm">
                            <div>
                                <span className="text-gray-500">
                                    {t('period')}:
                                </span>{' '}
                                {detailsPayout.period_start} –{' '}
                                {detailsPayout.period_end}
                            </div>
                            <div>
                                <span className="text-gray-500">
                                    {t('covered_range')}:
                                </span>{' '}
                                {coveredRange
                                    ? `${formatFromUTC(
                                          coveredRange.from,
                                          'DD/MM/YYYY'
                                      )} – ${formatFromUTC(
                                          coveredRange.to,
                                          'DD/MM/YYYY'
                                      )}`
                                    : '-'}
                            </div>
                            <div>
                                <span className="text-gray-500">
                                    {t('amount')}:
                                </span>{' '}
                                <span className="font-semibold">
                                    {formatCurrency(detailsPayout.amount)}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-500">
                                    {t('commission_count')}:
                                </span>{' '}
                                {commissionItems.length}
                            </div>
                        </div>
                    )}

                    {renderLineItemsState() || (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="border-b text-left text-gray-500">
                                        <th className="py-1 pr-2">
                                            {t('invoice')}
                                        </th>
                                        <th className="py-1 pr-2">
                                            {t('customer')}
                                        </th>
                                        <th className="py-1 pr-2">
                                            {t('paid_at')}
                                        </th>
                                        <th className="py-1 pr-2 text-right">
                                            {t('revenue')}
                                        </th>
                                        <th className="py-1 pr-2 text-right">
                                            {t('commission')}
                                        </th>
                                        <th className="py-1 pr-2">
                                            {t('status')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {commissionItems.map((item) => (
                                        <tr
                                            key={item.commission_id}
                                            className="border-b last:border-0"
                                        >
                                            <td className="py-1 pr-2">
                                                {item.invoice_id
                                                    ? `#${item.invoice_id}`
                                                    : '-'}
                                            </td>
                                            <td className="py-1 pr-2">
                                                {item.customer_name ||
                                                    item.customer_email ||
                                                    '-'}
                                            </td>
                                            <td className="py-1 pr-2">
                                                {item.effective_payment_at ||
                                                item.invoice_payment_at
                                                    ? formatFromUTC(
                                                          item.effective_payment_at ||
                                                              item.invoice_payment_at,
                                                          'DD/MM/YYYY'
                                                      )
                                                    : '-'}
                                            </td>
                                            <td className="py-1 pr-2 text-right">
                                                {formatCurrency(
                                                    item.revenue_amount
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-right font-medium">
                                                {formatCurrency(
                                                    item.commission_amount
                                                )}
                                            </td>
                                            <td className="py-1 pr-2">
                                                {item.reversed_after_payout_at ? (
                                                    <Tooltip
                                                        title={t(
                                                            'reversed_after_payout_hint'
                                                        )}
                                                    >
                                                        <span className="text-red-600 font-medium whitespace-nowrap">
                                                            ⚠{' '}
                                                            {t(
                                                                'reversed_after_payout'
                                                            )}
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    t(item.status || 'pending')
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {commissionItems.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="py-3 text-center text-gray-400"
                                            >
                                                {t('no_data')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsPayout(null)}>
                        {t('close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Payouts
